import type { Nullable, RecordKey } from '@ricoh/ria.client.core/types/core/data.type';

import { ClientError } from '@ricoh/ria.client.core/errors';
import { isNewKey, newKeyTemplate, transformNewKey } from '@ricoh/ria.client.core/utils/data/key';
import { omit, pick } from '@ricoh/ria.client.core/utils/data/object';

import type { ListTransformToIndexRecordOptions, ListTransformToRecordOptions, ListTransformToRecordWithStatusOptions, RecordItemStatus } from './list.type';

/** List Transform. */
export const listTransform = {
  /**
   * Transform list to index record.
   *
   * @template TData Data type.
   * @template TTransformData TransformData type.
   * @template TKey TransformKey type.
   * @param {Nullable<TData[]>} list List.
   * @param {ListTransformToIndexRecordOptions<TData, TTransformData, TKey>} options Options.
   *
   * @returns {Record<number, TData>} Record of item.
   */
  toIndexRecord: <TData extends object, TTransformData extends object, TKey extends RecordKey>(
    list: Nullable<TData[]>,
    { pickKeys = [], omitKeys = [], transform, transformKey }: ListTransformToIndexRecordOptions<TData, TTransformData, TKey> = {},
  ): Record<TKey, TTransformData> => {
    const result: Record<RecordKey, TTransformData> = {};

    // * Transform list to record. Use index as key.
    list?.forEach((item, index) => {
      // * Transform data
      let transformedData: object = (transform ? transform(item, index) : item) ?? {};

      // * Pick and omit properties.
      transformedData = pick(transformedData, ...pickKeys);
      transformedData = omit(transformedData, ...omitKeys);

      // * Transform key
      const key = transformKey ? transformKey(item, index) : (index as TKey);
      result[key] = transformedData as TTransformData;
    });

    return result;
  },

  /**
   * Transform list to record.
   *
   * @template TData Data type.
   * @template TTransformData TransformData type.
   * @template TKey Key type.
   * @param {Nullable<TData[]>} list List.
   * @param {ListTransformToRecordOptions<TData, TTransformData>} options Options.
   *
   * @returns {Record<RecordKey, TData>} Record of item.
   */
  toRecord: <TData extends object, TTransformData extends object, TKey extends RecordKey>(
    list: Nullable<TData[]>,
    { key = 'id' as keyof TData, pickKeys = [], omitKeys = [], transform }: ListTransformToRecordOptions<TData, TTransformData> = {},
  ): Record<TKey, TTransformData> => {
    const result: Record<RecordKey, TTransformData> = {};
    const transformedKeyMapping: Record<RecordKey, RecordKey> = {};

    // * Build new key mapping.
    list?.forEach((item, index) => {
      const keyValue = item[key];
      if (typeof keyValue !== 'string' && typeof keyValue !== 'number' && typeof keyValue !== 'symbol') {
        throw new ClientError({
          module: 'utility',
          message: `Invalid value of key`,
          cause: { item, key },
        });
      }

      if (!isNewKey(keyValue)) {
        return;
      }

      transformedKeyMapping[keyValue] = transformNewKey(keyValue, () => newKeyTemplate(index));
    });

    // * Transform list to record.
    list?.forEach((item, index) => {
      const keyValue = item[key];
      if (typeof keyValue !== 'string' && typeof keyValue !== 'number' && typeof keyValue !== 'symbol') {
        throw new ClientError({
          module: 'utility',
          message: `Invalid value of key`,
          cause: { item, key },
        });
      }

      // * Transform data
      let transformedData: object = (transform ? transform(item, index, transformedKeyMapping) : item) ?? {};

      // * Pick and omit properties.
      transformedData = pick(transformedData, ...pickKeys);
      transformedData = omit(transformedData, ...omitKeys);

      const transformedKey = transformedKeyMapping[keyValue];
      result[transformedKey ?? keyValue] = transformedData as TTransformData;
    });

    return result;
  },

  /**
   * Transform list to record With status flag.
   *
   * @template TTransformData TransformData type.
   * @template TData Data type.
   * @template TKey Key type.
   * @param {Nullable<TData[]>} list List.
   * @param {ListTransformToRecordWithStatusOptions<TData, TTransformData>} options Options.
   *
   * @returns {Record<RecordKey, TData>} Record of item.
   */
  toRecordWithEditStatus: <TData extends object, TTransformData extends object, TKey extends RecordKey>(
    list: Nullable<TData[]>,
    {
      key = 'id' as keyof TData,
      startKeyIndex = 0,
      status = {
        update: 'change',
        delete: 'remove',
      },
      pickKeys = [],
      omitKeys = [],
      initialList = [],
      transform,
    }: ListTransformToRecordWithStatusOptions<TData, TTransformData> = {},
  ): Record<TKey, TTransformData> => {
    const result: Record<RecordKey, TTransformData> = {};
    const transformedKeyMapping: Record<RecordKey, RecordKey> = {};

    // * Build new key mapping.
    list?.forEach((item) => {
      const keyValue = item[key];
      if (typeof keyValue !== 'string' && typeof keyValue !== 'number' && typeof keyValue !== 'symbol') {
        throw new ClientError({
          module: 'utility',
          message: `Invalid value of key`,
          cause: { item, key },
        });
      }

      if (!isNewKey(keyValue)) {
        return;
      }

      const index = Object.keys(transformedKeyMapping).length;
      transformedKeyMapping[keyValue] = transformNewKey(keyValue, () => newKeyTemplate(index + startKeyIndex));
    });

    // * Transform
    const transformData = (item: TData, index: number, itemStatus: RecordItemStatus) => {
      // * Transform data
      let transformedData: object =
        (transform
          ? transform(item, index, {
              initialList,
              status: itemStatus,
              transformedKeyMapping,
            })
          : item) ?? {};

      // * Pick and omit properties.
      transformedData = pick(transformedData, ...pickKeys);
      transformedData = omit(transformedData, '$is' as keyof TData, ...omitKeys);

      return transformedData;
    };

    // * Excluded new item list from initial list.
    const excludedNewItem = initialList.filter((item) => {
      const keyValue = item[key];
      if (typeof keyValue !== 'string' && typeof keyValue !== 'number' && typeof keyValue !== 'symbol') {
        throw new ClientError({
          module: 'utility',
          message: `Invalid value of key`,
          cause: { item, key },
        });
      }

      return !isNewKey(keyValue);
    });

    // * Transform list to record.
    list?.forEach((item, index) => {
      const keyValue = item[key];
      if (typeof keyValue !== 'string' && typeof keyValue !== 'number' && typeof keyValue !== 'symbol') {
        throw new ClientError({
          module: 'utility',
          message: `Invalid value of key`,
          cause: { item, key },
        });
      }

      // * If not new item and in initial list -> update.
      if (!isNewKey(keyValue) && excludedNewItem.some((initialItem) => initialItem[key] === keyValue)) {
        result[keyValue] = {
          $is: status.update,
          ...transformData(item, index, 'update'),
        } as TTransformData;

        return;
      }

      // * If new item and not in initial list -> new
      const transformedKey = transformedKeyMapping[keyValue];
      result[transformedKey ?? keyValue] = transformData(item, index, 'new') as TTransformData;
    });

    // * If in initial list but not in list -> delete.
    const resultKeys = Object.keys<RecordKey>(result);
    excludedNewItem.forEach((item, index) => {
      const keyValue = item[key];
      if (typeof keyValue !== 'string' && typeof keyValue !== 'number' && typeof keyValue !== 'symbol') {
        throw new ClientError({
          module: 'utility',
          message: `Invalid value of key`,
          cause: { item, key },
        });
      }

      // * Skip if in result.
      if (resultKeys.includes(keyValue)) {
        return;
      }

      result[keyValue] = {
        $is: status.delete,
        ...transformData(item, index, 'delete'),
      } as TTransformData;
    });

    return result;
  },
};
